/**
 * @author Hiroki Moto
 * @description https://marvelapp.com/c4fj1hh/screen/58977000
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import clone from 'ramda/src/clone';
import find from 'ramda/src/find';
import propEq from 'ramda/src/propEq';
import queryString from 'query-string';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import Filter from '@components/@core/Filter';
import Pagination from '@components/@core/Pagination';
import ResponsiveTabs from '@components/@core/ResponsiveTabs';
import Search from '@components/@core/Search';
import Tab from '@components/@core/Tab';
import Status from '@components/LibraryStatus';
import * as HabitCard from '@components/LibraryToDoHabit';
import * as HabitScheduleCard from '@components/LibraryToDoHabitSchedule';
import * as ScoreCard from '@components/LibraryToDoScoreCard';
import AppActions from '~/actions/app';
import DevelopActions from '~/actions/develop';
import { ToDoTypes, LibraryTodoTabs } from '~/services/config';
import withNavigate from '~/hocs/withNavigate';

class ToDo extends Component {
    constructor(props) {
        super(props);
        // Determine active tab from URL path or search params
        const urlPath = props.location?.pathname || '';
        let active = queryString.parse(props.location?.search || '')?.active;

        // If no active param, determine from URL path
        if (!active) {
            if (urlPath.includes('/library/habits')) {
                active = 'habits';
            } else if (urlPath.includes('/library/quotas')) {
                active = 'quotas';
            } else if (urlPath.includes('/library/scorecards')) {
                active = 'scorecards';
            } else {
                active = 'habits'; // default
            }
        }

        this.state = { active, filter: '', page: 1, perPage: 25 };
    }

    componentDidMount() {
        // Note: this.props.history.action check is not needed in v6, always load data
        const { filter, active, page, perPage } = this.state;
        this.props.getTemplates(active, { search: filter, page, perPage });
    }

    handleModal = (e) => {
        this.props.toggleModal(e);
    };

    handlePagination = (page) => {
        const { active, filter, perPage } = this.state;
        this.props.getTemplates(active, { search: filter, page, perPage });
        this.setState({ page });
    };

    handlePer = (perPage) => {
        const { active, filter, page } = this.state;
        this.props.getTemplates(active, { search: filter, page, perPage });
        this.setState({ perPage });
    };

    handleSearch = (e) => {
        const { active, perPage } = this.state;
        this.props.getTemplates(active, { search: e.target.value, page: 1, perPage });
        this.setState({ filter: e.target.value, page: 1 });
    };

    handleTab = (e) => {
        const { perPage } = this.state;
        this.props.getTemplates(e.name, { page: 1, perPage });
        this.props.navigate({ search: `?active=${e.name}` });
        this.setState({ active: e.name, filter: '', page: 1 });
    };

    handleDeleteTemplate = (template) => {
        const { filter, active, page, perPage } = this.state;

        let target = 'template';
        let route = null;
        if (active == 'tracks') {
            target = 'track';
        } else if (active == 'scorecards') {
            target = 'scorecard';
        }

        this.props.deleteTemplate(target, 'delete', template.id, null, route, {
            type: 'GETLIBRARYTEMPLATESSEARCH_REQUEST',
            target: active,
            payload: { search: filter, page, perPage },
        });
    };

    handleSelectMenu = (event, card) => {
        const { active, filter, page } = this.state;
        const { companyId } = this.props;
        const disabled = ToDoTypes.filter((x) => x !== active);

        switch (event) {
            case 'assign': {
                const payload = {
                    type: 'Assign ToDo',
                    data: { before: { modules: [card], disabled }, after: [] },
                };
                this.props.toggleModal(payload);
                break;
            }
            case 'detail view': {
                this.props.navigate({
                    pathname: `/library/to-do/${active}/${card.id}/view`,
                    state: { filter, page },
                });
                break;
            }
            case 'edit': {
                this.props.navigate({
                    pathname: `/library/to-do/${active}/${card.id}/edit`,
                    state: { card },
                });
                break;
            }
            case 'quick assign': {
                this.props.toggleModal({
                    type: 'Quick Edit',
                    data: { before: { template: card, type: active, from: 'template', companyId } },
                    callBack: { onDelete: () => this.handleDeleteTemplate(card) },
                });
                break;
            }
            case 'delete':
                this.props.toggleModal({
                    type: 'Confirm',
                    data: { before: { title: 'Delete', body: 'Are you sure you want to delete this item?' } },
                    callBack: { onYes: () => this.handleDeleteTemplate(card) },
                });
                break;
            case 'duplicate':
                this.props.toggleModal({
                    type: 'Duplicate Module',
                    data: { before: { type: active, object: card } },
                });
                break;
            case 'attach to scorecard':
                this.props.toggleModal({
                    type: 'Attach Library',
                    data: { before: { show: 'scorecards' } },
                    callBack: { onAttach: (e) => this.handleScorecardQuota(e.templates[0], card) },
                });
                break;
            case 'attach to program':
                this.props.toggleModal({
                    type: 'Attach Library',
                    data: { before: { show: ['careers', 'certifications'], isProgram: true } },
                    callBack: { onAttach: (e) => this.handleProgramQuota(e, card) },
                });
                break;
            default:
                break;
        }
    };

    handleScorecardQuota = (e, card) => {
        const quotas = clone(e.data.quotas);
        const duplicated = find(propEq('quota_template_id', card.id), quotas);
        if (duplicated) {
            toast.error('The selected Quota is already in the Scorecard', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
        } else {
            this.props.navigate({
                pathname: `/library/to-do/scorecards/${e.id}/edit`,
                state: { card },
            });
        }
    };

    handleProgramQuota = (e, card) => {
        const program = e.templates[0];
        const quotas = clone(program.data.levels[e.level].quotas);
        const duplicated = find(propEq('quota_template_id', card.id), quotas);
        if (duplicated) {
            toast.error('The selected Quota is already in the Scorecard', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
        } else {
            this.props.getProgramDetail(program, e.type, {
                pathname: `/library/programs/${e.type}/${program.id}/edit`,
                state: { card, level: e.level },
            });
        }
    };

    handleToggle = (id) => {
        const { active, filter, page } = this.state;
        this.props.navigate({
            pathname: `/library/to-do/${active}/${id}/view`,
            state: { filter, page },
        });
    };

    handleAdd = () => {
        const { active } = this.state;
        this.props.navigate(`/library/to-do/${active}/new`);
    };

    handleOpenQuota = (quota) => {
        const payload = { type: 'Add New Quota', data: { before: { quota } } };
        this.props.toggleModal(payload);
    };

    render() {
        const { active } = this.state;
        const { userRole, templates } = this.props;
        let addTitle = 'Habit';
        if (active == 'habitslist') addTitle = 'Habit Schedule';
        if (active == 'scorecards') addTitle = 'Scorecard';

        return (
            <ErrorBoundary>
                <Filter add={addTitle} onAdd={this.handleAdd} />
                <div className="card px-0 px-sm-4">
                    <div className="d-h-between px-4 px-sm-0">
                        <span className="dsl-b22 bold">To Dos</span>
                        <Search onChange={this.handleSearch} />
                    </div>
                    <ResponsiveTabs className="px-4 px-sm-0 to-dos-tabs" active={active} data={LibraryTodoTabs} getId={(e) => e['name']} getValue={(e) => e['label']} onTab={this.handleTab} />
                    <Tab eventKey="habits" active={active}>
                        <Status type="Habits" showToggle counts={templates[active].total} />
                        <HabitCard.List role={userRole} data={templates[active].data} onModal={this.handleModal} onSelect={this.handleSelectMenu} onToggle={this.handleToggle} />
                    </Tab>
                    <Tab eventKey="habitslist" active={active}>
                        <Status type="Habits Schedules" showToggle counts={templates[active].total} />
                        <HabitScheduleCard.List role={userRole} data={templates[active].data} onModal={this.handleModal} onSelect={this.handleSelectMenu} onToggle={this.handleToggle} />
                    </Tab>
                    <Pagination per={templates[active].per_page} current={templates[active].current_page} total={templates[active].last_page} onChange={this.handlePagination} onPer={this.handlePer} />
                </div>
            </ErrorBoundary>
        );
    }
}

ToDo.propTypes = {
    userRole: PropTypes.number,
    templates: PropTypes.any,
    getTemplates: PropTypes.func,
    deleteTemplate: PropTypes.func,
    toggleModal: PropTypes.func,
};

ToDo.defaultProps = {
    userRole: 1,
    templates: {},
    getTemplates: () => {},
    getProgramDetail: () => {},
    deleteTemplate: () => {},
    toggleModal: () => {},
};

const mapStateToProps = (state) => ({
    userRole: state.app.app_role_id,
    templates: state.develop.templates,
    companyId: state.app.company_info.id,
});

const mapDispatchToProps = (dispatch) => ({
    getProgramDetail: (payload, type, route) => dispatch(DevelopActions.libraryprogramdetailRequest(payload, type, route)),
    getTemplates: (target, payload) => dispatch(DevelopActions.getlibrarytemplatessearchRequest(target, payload)),
    deleteTemplate: (target, event, id, data, route, after) => dispatch(DevelopActions.libraryeventRequest(target, event, id, data, route, after)),
    toggleModal: (e) => dispatch(AppActions.modalRequest(e)),
});

const ToDoWithNavigate = withNavigate(ToDo);

export default connect(mapStateToProps, mapDispatchToProps)(ToDoWithNavigate);
